/**
 * useWorkspace Hook - Workspace Management
 *
 * Custom hook for workspace CRUD operations and state management.
 * Uses atomic Zustand selectors for optimal performance.
 *
 * @module hooks/useWorkspace
 */

import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useGeoMarkupStore } from '../store';
import {
  selectExtendedWorkspaces,
  selectWorkspaceIsLoading,
  selectWorkspaceIsCreating,
  selectWorkspaceError,
  selectCurrentWorkspaceId,
  selectWorkspaces,
  selectNeedsWorkspaceOnboarding,
} from '../store/selectors';
import { apiClient } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import type { WorkspaceListItem, WorkspaceRole } from '../types/workspace';
import type {
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
} from '../types/api/requests';
import type {
  ListWorkspacesResponse,
  CreateWorkspaceResponse,
  UpdateWorkspaceResponse,
  DeleteWorkspaceResponse,
} from '../types/api/responses';

interface CreateWorkspaceResult {
  success: boolean;
  workspaceId: string;
  shortId: string;
  error?: string;
}

interface UpdateWorkspaceResult {
  success: boolean;
  error?: string;
}

interface DeleteWorkspaceResult {
  success: boolean;
  error?: string;
  /**
   * Where to navigate after a successful delete: shortId of the first
   * remaining workspace, or `null` when none are left (→ onboarding).
   * Absent on failure.
   */
  nextWorkspaceShortId?: string | null;
}

/**
 * Hook for workspace data management and CRUD operations
 */
export function useWorkspace() {
  // ===== ATOMIC SUBSCRIPTIONS =====
  const extendedWorkspaces = useGeoMarkupStore(selectExtendedWorkspaces);
  const basicWorkspaces = useGeoMarkupStore(selectWorkspaces);
  const currentWorkspaceId = useGeoMarkupStore(selectCurrentWorkspaceId);
  const isLoading = useGeoMarkupStore(selectWorkspaceIsLoading);
  const isCreating = useGeoMarkupStore(selectWorkspaceIsCreating);
  const error = useGeoMarkupStore(selectWorkspaceError);
  const needsOnboarding = useGeoMarkupStore(selectNeedsWorkspaceOnboarding);

  // ===== ACTIONS (stable references) =====
  const actions = useGeoMarkupStore(
    useShallow((state) => ({
      setExtendedWorkspaces: state.workspace.setExtendedWorkspaces,
      removeExtendedWorkspace: state.workspace.removeExtendedWorkspace,
      setIsLoading: state.workspace.setIsLoading,
      setIsCreating: state.workspace.setIsCreating,
      setError: state.workspace.setError,
      clearError: state.workspace.clearError,
      setCurrentWorkspace: state.auth.setCurrentWorkspace,
      checkAuth: state.auth.checkAuth,
      patchAuthWorkspace: state.auth.patchAuthWorkspace,
    }))
  );

  // ===== DERIVED STATE =====
  const workspaces = useMemo(() => {
    // Use extended workspaces if available, otherwise map basic workspaces
    if (extendedWorkspaces.length > 0) {
      return extendedWorkspaces;
    }
    // Convert basic workspaces to extended format for compatibility
    return basicWorkspaces.map(
      (workspace): WorkspaceListItem => ({
        workspaceId: workspace.id,
        shortId: workspace.shortId,
        name: workspace.name,
        description: workspace.description,
        isOwner: workspace.isOwner,
        role: workspace.role,
        memberCount: 0,
        autoStartTasksEnabled: false,
        isMapConfigured: false,
        mapSettings: null,
        createdAt: new Date().toISOString(),
      })
    );
  }, [extendedWorkspaces, basicWorkspaces]);

  const currentWorkspace = useMemo(() => {
    return workspaces.find(
      (workspace) => workspace.workspaceId === currentWorkspaceId
    );
  }, [workspaces, currentWorkspaceId]);

  const hasWorkspaces = workspaces.length > 0;
  const workspaceCount = workspaces.length;

  // ===== FETCH WORKSPACES =====
  const fetchWorkspaces = useCallback(async () => {
    actions.setIsLoading(true);
    actions.clearError();

    try {
      // Refresh auth which includes workspace list with descriptions
      await actions.checkAuth();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch workspaces';
      actions.setError(message);
    } finally {
      actions.setIsLoading(false);
    }
  }, [actions]);

  // ===== FETCH WORKSPACES WITH SORTING/FILTERING =====
  const fetchWorkspacesWithSort = useCallback(
    async (sortBy?: string, sortDesc?: boolean, role?: WorkspaceRole) => {
      actions.setIsLoading(true);
      actions.clearError();

      try {
        const userId = useGeoMarkupStore.getState().auth.user?.id;
        if (!userId) {
          actions.setError('User not authenticated');
          return;
        }

        const params = new URLSearchParams();
        params.append('userId', userId);
        if (sortBy) params.append('sortBy', sortBy);
        if (sortDesc !== undefined) params.append('sortDesc', String(sortDesc));
        if (role) params.append('role', role);

        const response = await apiClient.get<ListWorkspacesResponse>(
          `${API_ENDPOINTS.WORKSPACES.LIST}?${params.toString()}`
        );

        actions.setExtendedWorkspaces(response.data.workspaces);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to fetch workspaces';
        actions.setError(message);
      } finally {
        actions.setIsLoading(false);
      }
    },
    [actions]
  );

  // ===== CREATE WORKSPACE =====
  const createWorkspace = useCallback(
    async (data: {
      name: string;
      description?: string;
    }): Promise<CreateWorkspaceResult> => {
      actions.setIsCreating(true);
      actions.clearError();

      try {
        const userId = useGeoMarkupStore.getState().auth.user?.id;
        if (!userId) {
          return {
            success: false,
            workspaceId: '',
            shortId: '',
            error: 'User not authenticated',
          };
        }

        const requestData: CreateWorkspaceRequest = {
          name: data.name,
          description: data.description || null,
          ownerId: userId,
          mapSettings: null,
        };

        const response = await apiClient.post<CreateWorkspaceResponse>(
          API_ENDPOINTS.WORKSPACES.CREATE,
          requestData
        );

        const createdWorkspace = response.data.workspace;

        // Refresh auth to get updated workspace list (includes session token refresh)
        await actions.checkAuth();

        // Patch extended workspaces with the created workspace from response
        const state = useGeoMarkupStore.getState();
        const currentExtended = state.workspace.extendedWorkspaces;
        if (
          !currentExtended.some(
            (ws) => ws.workspaceId === createdWorkspace.workspaceId
          )
        ) {
          actions.setExtendedWorkspaces([...currentExtended, createdWorkspace]);
        }

        // Set the newly created workspace as current
        actions.setCurrentWorkspace(createdWorkspace.workspaceId);

        return {
          success: true,
          workspaceId: createdWorkspace.workspaceId,
          shortId: createdWorkspace.shortId,
        };
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to create workspace';
        actions.setError(message);
        return { success: false, workspaceId: '', shortId: '', error: message };
      } finally {
        actions.setIsCreating(false);
      }
    },
    [actions]
  );

  // ===== UPDATE WORKSPACE =====
  const updateWorkspace = useCallback(
    async (
      workspaceId: string,
      data: UpdateWorkspaceRequest
    ): Promise<UpdateWorkspaceResult> => {
      actions.clearError();

      try {
        const response = await apiClient.put<UpdateWorkspaceResponse>(
          API_ENDPOINTS.WORKSPACES.UPDATE(workspaceId),
          data
        );

        const updatedWorkspace = response.data.workspace;

        // Patch auth.workspaces directly (avoids checkAuth which triggers ProtectedRoute loader)
        actions.patchAuthWorkspace(updatedWorkspace.workspaceId, {
          name: updatedWorkspace.name,
          description: updatedWorkspace.description,
        });

        // Patch extended workspaces with the updated workspace from response
        const state = useGeoMarkupStore.getState();
        const currentExtended = state.workspace.extendedWorkspaces;
        actions.setExtendedWorkspaces(
          currentExtended.map((ws) =>
            ws.workspaceId === updatedWorkspace.workspaceId
              ? updatedWorkspace
              : ws
          )
        );

        return { success: true };
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update workspace';
        actions.setError(message);
        return { success: false, error: message };
      }
    },
    [actions]
  );

  // ===== DELETE WORKSPACE =====
  const deleteWorkspace = useCallback(
    async (workspaceId: string): Promise<DeleteWorkspaceResult> => {
      actions.clearError();

      try {
        await apiClient.delete<DeleteWorkspaceResponse>(
          API_ENDPOINTS.WORKSPACES.DELETE(workspaceId)
        );

        // Refresh both workspace lists: checkAuth updates auth.workspaces and
        // re-resolves currentWorkspaceId (to the first remaining one, or null
        // when none are left); removeExtendedWorkspace drops the deleted entry
        // from the extended list that backs the workspace switcher.
        await actions.checkAuth();
        actions.removeExtendedWorkspace(workspaceId);

        // Tell the caller where to navigate next (first remaining workspace,
        // or null → onboarding). Defensively exclude the deleted id: checkAuth
        // no-ops when an auth check is already in flight, and the backend may
        // briefly still echo the just-deleted workspace, so the raw list[0]
        // could otherwise point back at the workspace we just removed.
        const remainingWorkspaces = useGeoMarkupStore
          .getState()
          .auth.workspaces.filter((workspace) => workspace.id !== workspaceId);
        return {
          success: true,
          nextWorkspaceShortId: remainingWorkspaces[0]?.shortId ?? null,
        };
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to delete workspace';
        actions.setError(message);
        return { success: false, error: message };
      }
    },
    [actions]
  );

  return {
    // State
    workspaces,
    currentWorkspaceId,
    currentWorkspace,
    isLoading,
    isCreating,
    error,
    hasWorkspaces,
    workspaceCount,
    needsOnboarding,

    // Actions
    fetchWorkspaces,
    fetchWorkspacesWithSort,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    setCurrentWorkspace: actions.setCurrentWorkspace,
    clearError: actions.clearError,
  };
}
